import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button } from '@components/Button/Button';
import { Card } from '@components/common/Card/Card';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { UTable } from '@components/common/UTable/UTable';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useGetDictionaryQuery } from '@/api/dictionaries/api';
import { Dictionary } from '@/api/dictionaries/types';
import {
  useAddFacilityMutation,
  useAddOrganizationMutation,
  useBlockOrganizationMutation,
  useDeleteFacilityMutation,
  useDeleteOrganizationMutation,
  useGetOrganizationByIdQuery,
  useLazyGetOrganizationIdSetQuery,
  useUnblockOrganizationMutation,
  useUpdateFacilityMutation,
  useUpdateOrganizationMutation,
} from '@/api/organizations/api';
import { useGetRolesQuery } from '@/api/roles/api';
import { RoleResponse } from '@/api/roles/types';
import { orgCardSchema } from '@/pages/Administration/components/organizations/card/card.config';
import {
  childrenOrgsTableConfig,
  structuralTableConfig,
} from '@/pages/Administration/components/organizations/card/columns.config';
import {
  createFacilityModalSchema,
  createOrgModalSchema,
  createStructuralModalSchema,
} from '@/pages/Administration/components/organizations/card/modal.config';
import { createRegionOrgModalSchema } from '@/pages/Administration/components/organizations/modal.config';
import { OrganizationLevelType, OrganizationStatus } from '@/shared/organizations/enums';
import { hasPermission } from '@/shared/security/hasPermission';
import { administration } from '@/shared/security/permissions/administration';
import { withSecurity } from '@/shared/security/withSecurity';
import { processFacilities } from '@/utils/utils';
import { message } from 'antd';
import { UserStatus } from '@/shared/users/enums';

export const OrganizationCard = withSecurity(({ auth }) => {
  const [isOrgEditModalOpen, setIsOrgEditModalOpen] = useState(false);

  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isUnblockModalOpen, setIsUnblockModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [isOpenCreateOrgModal, setIsOpenCreateOrgModal] = useState(false);
  const [isOpenCreateStructuralModal, setIsOpenCreateStructuralModal] = useState(false);
  const [isOpenCreateFacilityModal, setIsOpenCreateFacilityModal] = useState(false);
  const [isOpenEditStructuralModal, setIsOpenEditStructuralModal] = useState(false);
  const [isOpenEditFacilityModal, setIsOpenEditFacilityModal] = useState(false);

  const [editRecord, setEditRecord] = useState<any | null>(null);

  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: record,
    isLoading,
    isFetching,
  } = useGetOrganizationByIdQuery({ id } as { id: string });
  const { data: allRoles, isLoading: isRolesLoading } = useGetRolesQuery({});

  const [getParent, { data: parentOrg, isLoading: isParentLoading }] =
    useLazyGetOrganizationIdSetQuery();
  const [addOrganization, { isLoading: isOrgSaving }] = useAddOrganizationMutation();
  const [updateOrganization, { isLoading: isOrgUpdating }] =
    useUpdateOrganizationMutation();
  const [addFacility, { isLoading: isFacilitySaving }] = useAddFacilityMutation();
  const [updateFacility, { isLoading: isFacilityUpdating }] = useUpdateFacilityMutation();

  const [deleteFacility, { isLoading: isFacilityDeleting }] = useDeleteFacilityMutation();
  const [deleteOrganization, { isLoading: isOrganizationDeleting }] =
    useDeleteOrganizationMutation();

  const [blockOrganization, { isLoading: isOrganizationBlocking }] =
    useBlockOrganizationMutation();
  const [unblockOrganization, { isLoading: isOrganizationUnblocking }] =
    useUnblockOrganizationMutation();

  const { data: objectDictionary, isLoading: objectDictionaryLoading } =
    useGetDictionaryQuery({ dictionaryName: Dictionary.INDIVIDUAL_LOCATION });

  useEffect(() => {
    if (record?.parentIdsWithThis) {
      getParent([...record?.parentIdsWithThis, ...record?.nestedIds]);
    }
  }, [record]);

  const createOrganization = async (payload: any) => {
    const { regionParentId, ...restPayload } = payload;
    await addOrganization(restPayload).unwrap();
    setIsOpenCreateOrgModal(false);
    message.success('Организация успешно сохранена');
  };

  const createStructural = async (payload: any) => {
    const { latitude, longitude, ...restPayload } = payload;
    await addOrganization({
      ...restPayload,
      shortName: payload?.name,
      roleNames: record?.roleNames,
      coordinates: {
        coordinates: [parseFloat(latitude), parseFloat(longitude)],
        type: 'Point',
      },
    }).unwrap();
    setIsOpenCreateStructuralModal(false);
    message.success('Структурное подразделение успешно сохранено');
  };

  const onCreateFacility = (row: any) => {
    setEditRecord(row);
    setIsOpenCreateFacilityModal(true);
  };

  const onFacilityCreateSubmit = async (payload: any) => {
    const { orgId, parentId, ...restPayload } = payload;
    await addFacility({ ...restPayload, organizationId: parentId }).unwrap();
    setIsOpenCreateFacilityModal(false);
    setEditRecord(null);
    message.success('Объект успешно сохранен');
  };

  const handleDelete = async () => {
    if (editRecord?.level === 'FACILITY') {
      await deleteFacility(editRecord?.id).unwrap();
      message.success('Объект успешно удален');
    }
    if (editRecord?.level === OrganizationLevelType.STRUCTURAL_DIVISION) {
      await deleteOrganization(editRecord?.id).unwrap();
      message.success('Структурное подразделение успешно удалено');
    }
    setIsDeleteModalOpen(false);
    setEditRecord(false);
  };

  const onEditStructural = (row: any) => {
    setEditRecord(row);
    setIsOpenEditStructuralModal(true);
  };

  const onStructuralEditSubmit = async (payload: any) => {
    const { latitude, longitude, ...restPayload } = payload;
    await updateOrganization({
      ...restPayload,
      id: editRecord?.id,
      shortName: payload?.name,
      roleNames: record?.roleNames,
      coordinates: {
        coordinates: [parseFloat(latitude), parseFloat(longitude)],
        type: 'Point',
      },
    }).unwrap();
    setIsOpenEditStructuralModal(false);
    setEditRecord(null);
    message.success('Данные структурного подразделения успешно сохранены');
  };

  const onUpdateOrganizationSubmit = async (payload: any) => {
    const { regionParentId, ...restPayload } = payload;
    await updateOrganization({
      ...restPayload,
      id,
    }).unwrap();
    setIsOrgEditModalOpen(false);
    message.success('Данные организации успешно сохранены');
  };

  const onEditFacility = (row: any) => {
    setEditRecord(row);
    setIsOpenEditFacilityModal(true);
  };

  const onFacilityEditSubmit = async (payload: any) => {
    const { orgId, parentId, ...restPayload } = payload;
    await updateFacility({
      ...restPayload,
      organizationId: parentId,
      id: editRecord?.id,
    }).unwrap();
    setIsOpenEditFacilityModal(false);
    setEditRecord(null);
    message.success('Данные объекта успешно сохранены');
  };

  const handleBlock = async () => {
    await blockOrganization(id as string).unwrap();
    setIsBlockModalOpen(false);
    message.success('Организация успешно заблокирована');
  };

  const handleUnblock = async () => {
    await unblockOrganization(id as string).unwrap();
    setIsUnblockModalOpen(false);
    message.success('Организация успешно разблокирована');
  };

  return (
    <>
      <Modal
        mode={'confirm'}
        open={isBlockModalOpen}
        onCancel={() => setIsBlockModalOpen(false)}
        onSubmit={handleBlock}
        confirmLoading={isOrganizationBlocking}
        text={'Вы уверены, что хотите заблокировать организацию?'}
        submitText={'Да'}
        cancelText={'Нет'}
      />

      <Modal
        mode={'confirm'}
        open={isUnblockModalOpen}
        onCancel={() => setIsUnblockModalOpen(false)}
        onSubmit={handleUnblock}
        confirmLoading={isOrganizationUnblocking}
        text={'Вы уверены, что хотите разблокировать организацию?'}
        submitText={'Да'}
        cancelText={'Нет'}
      />
      <Modal
        mode={'form'}
        layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
        data={{ ...record, regionParentId: record?.parentIds?.[0] }}
        open={isOrgEditModalOpen}
        onCancel={() => {
          setIsOrgEditModalOpen(false);
        }}
        onSubmit={(payload) => onUpdateOrganizationSubmit(payload)}
        title={
          record?.level === OrganizationLevelType.DISTRICT
            ? 'Редактировать организацию районного уровня'
            : 'Редактировать организацию'
        }
        schema={
          record?.level === OrganizationLevelType.DISTRICT
            ? createRegionOrgModalSchema(parentOrg, allRoles as RoleResponse[])
            : createOrgModalSchema(parentOrg, allRoles as RoleResponse[])
        }
        destroyOnHidden={true}
      />
      {/*  Добавить организацию*/}
      {record?.level === OrganizationLevelType.DISTRICT && (
        <Modal
          mode={'form'}
          layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
          data={{ parentId: id, regionParentId: record.parentId }}
          open={isOpenCreateOrgModal}
          onCancel={() => {
            setIsOpenCreateOrgModal(false);
          }}
          onSubmit={(payload) => createOrganization(payload)}
          title={'Добавить организацию'}
          schema={createOrgModalSchema(parentOrg, allRoles as RoleResponse[])}
          confirmLoading={isOrgSaving}
          destroyOnHidden={true}
        />
      )}

      {record?.level === OrganizationLevelType.ORGANIZATION && (
        <>
          {/*  Добавить структурное подразделение */}
          <Modal
            mode={'form'}
            layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
            data={{ parentId: id }}
            open={isOpenCreateStructuralModal}
            onCancel={() => {
              setIsOpenCreateStructuralModal(false);
            }}
            onSubmit={(payload) => createStructural(payload)}
            title={'Добавить структурное подразделение'}
            schema={createStructuralModalSchema(parentOrg)}
            confirmLoading={isOrgSaving}
            destroyOnHidden={true}
          />

          {/*  Редактировать структурное подразделение*/}

          <Modal
            mode={'form'}
            layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
            data={{
              ...editRecord,
              parentId: id,
              latitude: editRecord?.coordinates?.coordinates?.[0],
              longitude: editRecord?.coordinates?.coordinates?.[1],
            }}
            open={isOpenEditStructuralModal}
            onCancel={() => {
              setIsOpenEditStructuralModal(false);
              setEditRecord(null);
            }}
            onSubmit={(payload) => onStructuralEditSubmit(payload)}
            title={'Редактировать структурное подразделение'}
            schema={createStructuralModalSchema(parentOrg)}
            confirmLoading={isOrgUpdating}
            destroyOnHidden={true}
          />

          {/*  Добавить объект */}
          <Modal
            mode={'form'}
            layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
            data={{ parentId: editRecord?.id, orgId: id }}
            open={isOpenCreateFacilityModal}
            onCancel={() => {
              setIsOpenCreateFacilityModal(false);
              setEditRecord(null);
            }}
            onSubmit={(payload) => onFacilityCreateSubmit(payload)}
            title={'Добавить объект'}
            schema={createFacilityModalSchema(parentOrg, objectDictionary)}
            confirmLoading={isFacilitySaving}
            destroyOnHidden={true}
          />

          {/*  Редактировать объект */}
          <Modal
            mode={'form'}
            layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
            data={{ ...editRecord, parentId: editRecord?.parentId, orgId: id }}
            open={isOpenEditFacilityModal}
            onCancel={() => {
              setIsOpenEditFacilityModal(false);
              setEditRecord(null);
            }}
            onSubmit={(payload) => onFacilityEditSubmit(payload)}
            title={'Редактировать объект'}
            schema={createFacilityModalSchema(parentOrg, objectDictionary)}
            confirmLoading={isFacilityUpdating}
            destroyOnHidden={true}
          />
          <Modal
            mode={'confirm'}
            open={isDeleteModalOpen}
            onCancel={() => {
              setIsDeleteModalOpen(false);
              setEditRecord(null);
            }}
            onSubmit={handleDelete}
            confirmLoading={isOrganizationDeleting || isFacilityDeleting}
            text={'Вы уверены, что хотите удалить выбранные данные?'}
            submitText={'Да'}
            cancelText={'Нет'}
          />
        </>
      )}

      <PageTitle title={record?.name} />

      <Card
        viewMode
        loading={isLoading || isFetching || isRolesLoading || isParentLoading}
        schema={orgCardSchema(
          allRoles as RoleResponse[],
          parentOrg as any[],
          record?.level,
        )}
        data={{
          ...record,
          regionParentId:
            record?.level === OrganizationLevelType.ORGANIZATION
              ? record?.parentIds[1]
              : record?.parentId,
          districtParentId:
            record?.level === OrganizationLevelType.ORGANIZATION ? record?.parentId : '',
        }}
        actions={{
          onBack: () => navigate(-1),
          ...(record?.status === OrganizationStatus.ACTIVE && {
            onEdit: {
              action: () => setIsOrgEditModalOpen(true),
              security: {
                permissions: administration.orgs.edit,
              },
            },
          }),
          extra: [
            ...(record?.status === OrganizationStatus.ACTIVE
              ? [
                  {
                    text: 'Заблокировать',
                    action: () => setIsBlockModalOpen(true),
                    security: {
                      permissions: administration.orgs.block,
                    },
                  },
                ]
              : []),
            ...(record?.status === OrganizationStatus.BLOCKED
              ? [
                  {
                    text: 'Разблокировать',
                    action: () => setIsUnblockModalOpen(true),
                    security: {
                      permissions: administration.orgs.unblock,
                    },
                  },
                ]
              : []),
          ],
        }}
      />
      <UTable
        actionColumnWidth={140}
        size={'small'}
        columns={
          record?.level === OrganizationLevelType.DISTRICT
            ? childrenOrgsTableConfig
            : structuralTableConfig
        }
        data={processFacilities(record?.children)}
        loading={isLoading || isFetching}
        expandable={{ childrenColumnName: 'facilities' }}
        style={{ marginTop: 50 }}
        actions={(row) => [
          ...(record.level === OrganizationLevelType.DISTRICT
            ? [
                {
                  icon: <EyeOutlined />,
                  action: (row: any) =>
                    navigate(`/administration/organizations/${row?.id}`),
                  security: {
                    permissions: administration.orgs.view,
                  },
                },
              ]
            : []),

          ...(record.level === OrganizationLevelType.ORGANIZATION &&
          row?.level === OrganizationLevelType.STRUCTURAL_DIVISION
            ? [
                {
                  tooltip: 'Добавить объект',
                  icon: <PlusOutlined />,
                  action: () => onCreateFacility(row),
                  security: {
                    permissions: administration.orgs.addFacility,
                  },
                },
              ]
            : []),
          ...(record.level === OrganizationLevelType.ORGANIZATION
            ? [
                {
                  icon: <EditOutlined />,
                  action:
                    row?.level === OrganizationLevelType.STRUCTURAL_DIVISION
                      ? () => onEditStructural(row)
                      : () => onEditFacility(row),
                  security: {
                    permissions: [
                      administration.orgs.editDepartment,
                      administration.orgs.editFacility,
                    ],
                  },
                },
                {
                  icon: <DeleteOutlined />,
                  action: () => {
                    setEditRecord(row);
                    setIsDeleteModalOpen(true);
                  },
                  security: {
                    permissions: [
                      administration.orgs.deleteDepartment,
                      administration.orgs.deleteFacility,
                    ],
                  },
                },
              ]
            : []),
        ]}
      />
      <div className={'bottom-buttons'}>
        <Button
          type={'primary'}
          text={'Добавить'}
          onClick={
            record?.level === OrganizationLevelType.DISTRICT
              ? () => setIsOpenCreateOrgModal(true)
              : () => setIsOpenCreateStructuralModal(true)
          }
          display={hasPermission(auth, {
            permissions: [
              administration.orgs.addDepartment,
              administration.orgs.addFacility,
            ],
          })}
        />
      </div>
    </>
  );
});
